import { DateTime } from "luxon";

import type { EventGuest } from "@/utils/event-queries";

export function composeAddress(values: {
  location_city?: null | string;
  location_state?: null | string;
  location_street?: null | string;
}): string {
  return [values.location_street, values.location_city, values.location_state]
    .filter(Boolean)
    .join(", ");
}

export function formatEventDate(startISO: string): string {
  const eventDate = DateTime.fromISO(startISO);
  if (!eventDate.isValid) return "select date";
  const now = DateTime.now();
  const dateSuffix =
    eventDate.toFormat("MMMM d").toLowerCase() + ordinal(eventDate.day);
  const dayName = eventDate.toFormat("EEEE").toLowerCase();

  const minutesFromNow = eventDate.diff(now, "minutes").minutes;
  if (minutesFromNow >= -60 && minutesFromNow <= 30) return "now";

  if (eventDate.hasSame(now, "day"))
    return eventDate.hour >= 19 ? "tonight" : "today";
  if (eventDate.hasSame(now.plus({ days: 1 }), "day"))
    return `tomorrow, ${dateSuffix}`;

  const daysAway = eventDate
    .startOf("day")
    .diff(now.startOf("day"), "days").days;
  if (daysAway > 0 && daysAway < 7) return `this ${dayName}, ${dateSuffix}`;
  return `${dayName}, ${dateSuffix}`;
}

export function formatGuestList(guests: EventGuest[]): string {
  const others = guests.filter((g) => !g.isSelf);
  const selfGoing = guests.some((g) => g.isSelf);
  const names = others.map((g) => g.name.split(" ")[0].toLowerCase());

  if (selfGoing) {
    if (names.length === 0) return "you're going";
    return `${joinNames(["you", ...names])} are going`;
  }

  if (names.length === 1) return `${names[0]} is going`;
  return `${joinNames(names)} are going`;
}

export function formatHostsBeingShown(hosts: string[]): string {
  const firstNames = hosts.map((h) => h.split(" ")[0]);
  const names =
    firstNames.length === 1
      ? firstNames[0]
      : `${firstNames.slice(0, -1).join(", ")} and ${firstNames[firstNames.length - 1]}`;
  return `by ${names}`;
}

export function formatPriceEditMode(cents: number | string): string {
  const n = typeof cents === "string" ? parseInt(cents, 10) || 0 : cents;
  if (n === 0) return "free";
  const dollars = n / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

export function formatPriceViewMode(
  priceCents: null | number,
  priceServiceFee = 0,
): string {
  const total = (priceCents ?? 0) + priceServiceFee;
  if (total === 0) return "free";
  const dollars = total / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

export function formatRsvpStatus(status: string): string {
  switch (status) {
    case "confirmed":
      return "going";
    case "pending":
      return "maybe";
    default:
      return status;
  }
}

export function formatTimeRange(
  startISO: null | string,
  endISO: null | string,
): string {
  if (!startISO) return "select time";
  const start = DateTime.fromISO(startISO);
  const end = endISO ? DateTime.fromISO(endISO) : null;
  if (!start.isValid) return "select time";
  const startStr = start.toFormat("h:mma").toLowerCase();
  if (!end?.isValid) return startStr;
  return `${startStr}–${end.toFormat("h:mma").toLowerCase()}`;
}

function joinNames(names: string[]): string {
  if (names.length <= 2) return names.join(" and ");
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

function ordinal(day: number) {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}
